import { ChangeDetectionStrategy, Component, OnInit, Signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideHouse } from '@ng-icons/lucide';

import { TextInputComponent } from 'app/home/common/components/forms/text-input/text-input.component';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { UnitNumberEntryQuoteFormInput } from 'app/home/create-quote/forms/unit-number-entry/unit-number-entry.types';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';

@Component({
  selector: 'app-unit-number-entry',
  imports: [ReactiveFormsModule, FormCardComponent, TextInputComponent, LockedQuoteAlertComponent],
  templateUrl: './unit-number-entry.component.html',
  providers: [provideIcons({ lucideHouse, lucideCircleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/unit_number_entry',
})
export class QuoteFormUnitNumberEntryComponent extends BaseQuoteForm<UnitNumberEntryQuoteFormInput> implements OnInit, Lockable {
  public formDataIn: UnitNumberEntryQuoteFormInput = this.quoteDraftStore.unitNumberEntryQuoteFormSelector();

  public formDefinition = {
    unitNumber: new FormControl<string | undefined>(undefined, Validators.required),
  };

  public unitNumberEntryForm = new FormGroup(this.formDefinition);

  public showFormRequiredError(): boolean {
    const control = this.unitNumberEntryForm.get('unitNumber');
    return !!control?.touched && control?.value === undefined;
  }

  public ngOnInit() {
    if (this.formDataIn) {
      this.unitNumberEntryForm.controls.unitNumber.setValue(this.formDataIn.unitNumber);
    }
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  public getFormControls() {
    return this.unitNumberEntryForm.controls;
  }

  private getFormValues(): Pick<QuoteDraftState, 'unitNumber'> {
    const { controls } = this.unitNumberEntryForm;
    const formValues = {
      unitNumber: controls.unitNumber.value ?? undefined,
    };

    return formValues;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.unitNumberEntryForm.markAllAsTouched();
    if (this.unitNumberEntryForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }

  public errorMessages = {
    unitNumber: {
      required: 'Please enter your unit number.',
    },
  };
}
